import { ref, watch, onBeforeUnmount } from 'vue';
import type { Ref } from 'vue';

type Serializer<T> = (value: T) => string;
type Deserializer<T> = (value: string) => T;

interface StorageOptions<T> {
  serializer?: Serializer<T>;
  deserializer?: Deserializer<T>;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function defaultSerialize<T>(value: T): string {
  return JSON.stringify(value);
}

function defaultDeserialize<T>(value: string): T {
  return JSON.parse(value) as T;
}

export function getLocalStorageValue<T>(
  key: string,
  fallback?: T,
  options?: StorageOptions<T>
): T | undefined {
  if (!isBrowser()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const deserializer = options?.deserializer ?? defaultDeserialize<T>;
    return deserializer(raw);
  } catch {
    return fallback;
  }
}

export function setLocalStorageValue<T>(key: string, value: T, options?: StorageOptions<T>): void {
  if (!isBrowser()) return;
  try {
    const serializer = options?.serializer ?? defaultSerialize<T>;
    window.localStorage.setItem(key, serializer(value));
  } catch {
    // no-op
  }
}

export function removeLocalStorageValue(key: string): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // no-op
  }
}

export function useLocalStorageRef<T>(
  key: string,
  defaultValue: T,
  options?: StorageOptions<T>
): { ref: Ref<T>; dispose: () => void } {
  const initial = getLocalStorageValue<T>(key, defaultValue, options);
  const state = ref(initial as T) as Ref<T>;

  const stop = watch(
    state,
    val => {
      setLocalStorageValue<T>(key, val, options);
    },
    { deep: true }
  );

  function handleStorage(e: StorageEvent) {
    if (e.storageArea !== window.localStorage) return;
    if (e.key !== key) return;
    if (e.newValue == null) {
      state.value = defaultValue;
      return;
    }
    try {
      const deserializer = options?.deserializer ?? defaultDeserialize<T>;
      state.value = deserializer(e.newValue);
    } catch {
      console.error('Error parsing localStorage value for key:', key);
      // Should we set the fallback value as `defaultValue` here?
      // ignore parse errors
    }
  }

  if (isBrowser()) {
    window.addEventListener('storage', handleStorage);
  }

  const dispose = () => {
    stop();
    if (isBrowser()) {
      window.removeEventListener('storage', handleStorage);
    }
  };

  onBeforeUnmount(dispose);

  return { ref: state, dispose };
}

export function useLocalStorage<T>(key: string, options?: StorageOptions<T>) {
  return {
    get: (fallback?: T) => getLocalStorageValue<T>(key, fallback, options),
    set: (value: T) => setLocalStorageValue<T>(key, value, options),
    remove: () => removeLocalStorageValue(key)
  };
}
